import React, { useState, useEffect } from 'react';
import { projectApi, userApi } from '../services/api';
import { Card, Button, Input, Select, Modal, Badge, Spinner } from '../components/UI';
import { Plus, PencilSimple, MapPin, Calendar, CurrencyDollar } from '@phosphor-icons/react';
import { format } from 'date-fns';

const STATUS_OPTIONS = ['PLANNING', 'ACTIVE', 'ON_HOLD', 'COMPLETED', 'CANCELLED'];

import { fmtCurrencyShort } from '../utils/currencyFormat';

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    name: '', client_name: '', location: '', start_date: '', end_date: '',
    contract_value: '', status: 'PLANNING', manager_id: '', team_member_ids: [], description: ''
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [projectsRes, usersRes] = await Promise.all([projectApi.list(), userApi.list()]);
      setProjects(projectsRes.data);
      setUsers(usersRes.data);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingProject(null);
    setFormData({
      name: '', client_name: '', location: '', start_date: '', end_date: '',
      contract_value: '', status: 'PLANNING', manager_id: '', team_member_ids: [], description: ''
    });
    setError('');
    setShowModal(true);
  };

  const openEditModal = (project) => {
    setEditingProject(project);
    setFormData({
      name: project.name,
      client_name: project.client_name,
      location: project.location,
      start_date: project.start_date ? project.start_date.split('T')[0] : '',
      end_date: project.end_date ? project.end_date.split('T')[0] : '',
      contract_value: project.contract_value || '',
      status: project.status,
      manager_id: project.manager_id || '',
      team_member_ids: project.team_member_ids || [],
      description: project.description || ''
    });
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      const data = {
        ...formData,
        contract_value: parseFloat(formData.contract_value) || 0,
        start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null,
      };

      if (editingProject) {
        await projectApi.update(editingProject.id, data);
      } else {
        await projectApi.create(data);
      }
      setShowModal(false);
      loadData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save project');
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'ACTIVE': return 'success';
      case 'PLANNING': return 'primary';
      case 'COMPLETED': return 'success';
      case 'ON_HOLD': return 'warning';
      case 'CANCELLED': return 'danger';
      default: return 'default';
    }
  };

  const managers = users.filter(u => ['IT Admin', 'Project Manager'].includes(u.role));

  if (loading) return <div className="flex justify-center py-8"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0A0A0A]">Projects</h1>
          <p className="text-[#666666]">Manage your construction projects</p>
        </div>
        <Button onClick={openCreateModal} data-testid="add-project-btn">
          <Plus className="w-4 h-4 mr-2" /> New Project
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {projects.map((project, idx) => (
          <Card key={project.id} className={`p-4 hover:shadow-md transition-shadow cursor-pointer animate-fade-in`} style={{animationDelay: `${idx * 0.05}s`}}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-semibold text-[#0A0A0A]">{project.name}</h3>
                <p className="text-sm text-[#666666]">{project.client_name}</p>
              </div>
              <Badge variant={getStatusColor(project.status)}>{project.status}</Badge>
            </div>
            
            <div className="space-y-2 text-sm text-[#666666]">
              <div className="flex items-center space-x-2">
                <MapPin className="w-4 h-4" />
                <span>{project.location || 'No location'}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4" />
                <span>{project.start_date ? format(new Date(project.start_date), 'MMM dd, yyyy') : 'No date'}</span>
              </div>
              <div className="flex items-center space-x-2">
                <CurrencyDollar className="w-4 h-4" />
                <span>{project.contract_value ? fmtCurrencyShort(project.contract_value) : 'No value'}</span>
              </div>
            </div>

            <div className="mt-4 pt-3 border-t border-[#E0E0E0] flex justify-end">
              <button onClick={() => openEditModal(project)} className="text-sm text-[#002FA7] hover:underline flex items-center">
                <PencilSimple className="w-4 h-4 mr-1" /> Edit
              </button>
            </div>
          </Card>
        ))}
        
        {projects.length === 0 && (
          <div className="col-span-full text-center py-12 text-[#666666]">
            <p>No projects yet. Create your first project!</p>
          </div>
        )}
      </div>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editingProject ? 'Edit Project' : 'Create Project'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <div className="p-3 bg-[#FF3333]/10 text-[#FF3333] rounded-lg text-sm">{error}</div>}
          
          <Input label="Project Name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required data-testid="project-name-input" />
          <Input label="Client Name" value={formData.client_name} onChange={(e) => setFormData({...formData, client_name: e.target.value})} required data-testid="project-client-input" />
          <Input label="Location" value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} required data-testid="project-location-input" />
          
          <div className="grid grid-cols-2 gap-4">
            <Input label="Start Date" type="date" value={formData.start_date} onChange={(e) => setFormData({...formData, start_date: e.target.value})} required data-testid="project-start-date-input" />
            <Input label="End Date" type="date" value={formData.end_date} onChange={(e) => setFormData({...formData, end_date: e.target.value})} data-testid="project-end-date-input" />
          </div>
          
          <Input label="Contract Value ($)" type="number" value={formData.contract_value} onChange={(e) => setFormData({...formData, contract_value: e.target.value})} data-testid="project-contract-input" />
          
          <Select label="Status" value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})} data-testid="project-status-select">            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </Select>
          
          <Select label="Project Manager" value={formData.manager_id} onChange={(e) => setFormData({...formData, manager_id: e.target.value})}>
            <option value="">-- Select Manager --</option>
            {managers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </Select>
          
          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="save-project-btn">{saving ? 'Saving...' : 'Save'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
